import React, { useEffect, useMemo, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Row,
  Col,
  Spinner,
} from "reactstrap";
import moment from "moment";
import IncomePeriodDatePicker from "./IncomePeriodDatePicker";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import IncomeService from "../../../Services/income/IncomeService";
import "./update-income-modal.scss";
import { getApiErrorMessage } from "../../../utils/apiErrorMessage";

const STATUS_OPTIONS = [
  "Pending",
  "Ongoing",
  "File Uploaded",
  "processed",
  "Completed",
  "Done",
];

function fileNameFromUrl(url) {
  if (!url) return null;
  try {
    const segment = String(url).split("/").pop() || "";
    return decodeURIComponent(segment.split("?")[0]) || null;
  } catch {
    return "Attached file";
  }
}

function toDate(value) {
  if (!value) return null;
  const parsed = moment(value);
  return parsed.isValid() ? parsed.toDate() : null;
}

function buildDefaults(record) {
  return {
    title: record?.title ?? "",
    description: record?.description ?? "",
    provider_id: record?.provider_id ? String(record.provider_id) : "",
    amount: record?.amount ?? "",
    currencies: record?.currencies ?? "",
    status: record?.status ?? "Pending",
    visibility: record?.visibility !== undefined ? Boolean(record.visibility) : true,
    period_start: record?.period_start
      ? moment(record.period_start).format("YYYY-MM-DD")
      : "",
    period_end: record?.period_end
      ? moment(record.period_end).format("YYYY-MM-DD")
      : "",
  };
}

const UpdateIncomeModal = ({ isOpen, toggle, income, onSaved }) => {
  const queryClient = useQueryClient();
  const incomeId = income?.id;
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [rawFile, setRawFile] = useState(null);
  const [rawUploadProgress, setRawUploadProgress] = useState(0);
  const [periodStartDate, setPeriodStartDate] = useState(null);
  const [periodEndDate, setPeriodEndDate] = useState(null);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({ defaultValues: buildDefaults(null) });

  const { data: detailResponse, isLoading: isDetailLoading } = useQuery({
    queryKey: ["income-detail", incomeId],
    queryFn: () => IncomeService.getIncomeById(incomeId),
    enabled: isOpen && !!incomeId,
  });

  const record = detailResponse?.data ?? income;

  const { data: providers, isLoading: isProvidersLoading } = useQuery({
    queryKey: ["providers"],
    queryFn: IncomeService.getProviders,
    enabled: isOpen,
  });

  useEffect(() => {
    if (!isOpen || !record) return;

    const defaults = buildDefaults(record);
    reset(defaults);
    setPeriodStartDate(toDate(record.period_start));
    setPeriodEndDate(toDate(record.period_end));
    setInvoiceFile(null);
    setRawFile(null);
    setRawUploadProgress(0);
  }, [isOpen, record, reset]);

  const invoiceLabel = useMemo(
    () => fileNameFromUrl(record?.file),
    [record?.file]
  );
  const rawLabel = useMemo(
    () => fileNameFromUrl(record?.raw_file),
    [record?.raw_file]
  );

  const saveMutation = useMutation({
    mutationFn: async (formValues) => {
      const formData = new FormData();
      Object.entries(formValues).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== "") {
          formData.append(key, value);
        }
      });

      if (invoiceFile) {
        formData.append("file", invoiceFile);
      }

      await IncomeService.updateIncome(incomeId, formData);

      if (rawFile) {
        await IncomeService.uploadRawFile(incomeId, rawFile, {
          onProgress: setRawUploadProgress,
          replaceExisting: !!(record?.raw_file || record?.has_raw_file),
        });
      }
    },
    onSuccess: () => {
      toast.success(
        rawFile
          ? "Income updated and raw CSV uploaded."
          : "Income updated successfully."
      );
      queryClient.invalidateQueries({ queryKey: ["incomes"] });
      queryClient.invalidateQueries({ queryKey: ["income-detail", incomeId] });
      queryClient.invalidateQueries({ queryKey: ["report"] });
      onSaved?.();
      toggle();
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, "Failed to update income"));
    },
  });

  const onSubmit = (data) => {
    const providerName =
      (providers || []).find((p) => String(p.id) === String(data.provider_id))
        ?.name || "Income";
    const periodLabel =
      data.period_start && data.period_end
        ? `${data.period_start} – ${data.period_end}`
        : data.period_start || "";
    const autoTitle = periodLabel
      ? `${providerName} ${periodLabel}`
      : providerName;

    saveMutation.mutate({
      ...data,
      title: record?.title || autoTitle,
      visibility: data.visibility ? "1" : "0",
    });
  };

  const isSaving = saveMutation.isPending;

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered className="inc-update-modal">
      <ModalHeader toggle={toggle}>
        Update income{record?.title ? ` · ${record.title}` : ""}
      </ModalHeader>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <ModalBody>
          {isDetailLoading ? (
            <div className="text-center py-4">
              <Spinner size="sm" color="primary" /> Loading income…
            </div>
          ) : (
            <>
              <div className="inc-update-modal__section">
                <p className="inc-update-modal__section-title">Details</p>
                <Row>
                  <Col md="12">
                    <FormGroup>
                      <Label for="provider_id">Provider</Label>
                      <Controller
                        name="provider_id"
                        control={control}
                        rules={{ required: "Provider is required" }}
                        render={({ field }) => (
                          <Input
                            {...field}
                            type="select"
                            id="provider_id"
                            disabled={isProvidersLoading}
                            invalid={!!errors.provider_id}
                          >
                            <option value="">Select provider</option>
                            {(providers || []).map((provider) => (
                              <option key={provider.id} value={String(provider.id)}>
                                {provider.name}
                              </option>
                            ))}
                          </Input>
                        )}
                      />
                      {errors.provider_id && (
                        <div className="text-danger small">{errors.provider_id.message}</div>
                      )}
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md="12">
                    <FormGroup>
                      <Label for="description">Description</Label>
                      <Controller
                        name="description"
                        control={control}
                        render={({ field }) => (
                          <Input
                            {...field}
                            type="textarea"
                            rows="2"
                            id="description"
                            placeholder="Optional notes"
                          />
                        )}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md="4">
                    <FormGroup>
                      <Label for="amount">Amount</Label>
                      <Controller
                        name="amount"
                        control={control}
                        rules={{ required: "Amount is required" }}
                        render={({ field }) => (
                          <Input
                            {...field}
                            type="number"
                            step="any"
                            id="amount"
                            invalid={!!errors.amount}
                          />
                        )}
                      />
                      {errors.amount && (
                        <div className="text-danger small">{errors.amount.message}</div>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md="4">
                    <FormGroup>
                      <Label for="currencies">Currency</Label>
                      <Controller
                        name="currencies"
                        control={control}
                        rules={{ required: "Currency is required" }}
                        render={({ field }) => (
                          <Input {...field} id="currencies" invalid={!!errors.currencies} />
                        )}
                      />
                      {errors.currencies && (
                        <div className="text-danger small">{errors.currencies.message}</div>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md="4">
                    <FormGroup>
                      <Label for="status">Status</Label>
                      <Controller
                        name="status"
                        control={control}
                        rules={{ required: "Status is required" }}
                        render={({ field }) => (
                          <Input {...field} type="select" id="status" invalid={!!errors.status}>
                            {STATUS_OPTIONS.map((status) => (
                              <option key={status} value={status}>
                                {status}
                              </option>
                            ))}
                          </Input>
                        )}
                      />
                      {errors.status && (
                        <div className="text-danger small">{errors.status.message}</div>
                      )}
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md="12">
                    <FormGroup>
                      <Label for="visibility">Visibility</Label>
                      <Controller
                        name="visibility"
                        control={control}
                        render={({ field }) => (
                          <Input
                            type="select"
                            id="visibility"
                            value={field.value ? "1" : "0"}
                            onChange={(e) => field.onChange(e.target.value === "1")}
                            onBlur={field.onBlur}
                            innerRef={field.ref}
                          >
                            <option value="0">Private — Admins &amp; Staff only (testing)</option>
                            <option value="1">Shared — Labels &amp; Artists can access</option>
                          </Input>
                        )}
                      />
                      <small className="text-muted d-block mt-1">
                        Private hides this income and its related distribution data from labels and artists.
                      </small>
                    </FormGroup>
                  </Col>
                </Row>
              </div>

              <div className="inc-update-modal__section">
                <p className="inc-update-modal__section-title">Period</p>
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label for="period_start">Period start</Label>
                      <Controller
                        name="period_start"
                        control={control}
                        rules={{ required: "Period start is required" }}
                        render={({ field }) => (
                          <div className="date-picker">
                            <IncomePeriodDatePicker
                              variant="modal"
                              selected={periodStartDate}
                              onChange={(date) => {
                                setPeriodStartDate(date);
                                field.onChange(date ? moment(date).format("YYYY-MM-DD") : "");

                                if (date) {
                                  const monthEnd = moment(date).endOf("month");
                                  setPeriodEndDate(monthEnd.toDate());
                                  setValue("period_end", monthEnd.format("YYYY-MM-DD"), {
                                    shouldValidate: true,
                                    shouldDirty: true,
                                  });
                                } else {
                                  setPeriodEndDate(null);
                                  setValue("period_end", "", { shouldValidate: true });
                                }
                              }}
                              selectsStart
                              startDate={periodStartDate}
                              endDate={periodEndDate}
                              maxDate={new Date()}
                              placeholder="Select start date"
                            />
                          </div>
                        )}
                      />
                      {errors.period_start && (
                        <div className="text-danger small">{errors.period_start.message}</div>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label for="period_end">Period end</Label>
                      <Controller
                        name="period_end"
                        control={control}
                        rules={{ required: "Period end is required" }}
                        render={({ field }) => (
                          <div className="date-picker">
                            <IncomePeriodDatePicker
                              variant="modal"
                              selected={periodEndDate}
                              onChange={(date) => {
                                setPeriodEndDate(date);
                                field.onChange(date ? moment(date).format("YYYY-MM-DD") : "");
                              }}
                              selectsEnd
                              startDate={periodStartDate}
                              endDate={periodEndDate}
                              minDate={periodStartDate}
                              placeholder="Select end date"
                            />
                          </div>
                        )}
                      />
                      {errors.period_end && (
                        <div className="text-danger small">{errors.period_end.message}</div>
                      )}
                    </FormGroup>
                  </Col>
                </Row>
              </div>

              <div className="inc-update-modal__section">
                <p className="inc-update-modal__section-title">Files</p>
                <Row>
                  <Col md="6">
                    <div className="inc-update-modal__file-card">
                      <Label for="invoice_file" className="fw-semibold">
                        Invoice / PDF
                      </Label>
                      <div className="inc-update-modal__file-status">
                        <span
                          className={`inc-update-modal__badge ${
                            record?.file || record?.has_invoice_file
                              ? "inc-update-modal__badge--ok"
                              : "inc-update-modal__badge--missing"
                          }`}
                        >
                          {record?.file || record?.has_invoice_file ? "Attached" : "Missing"}
                        </span>
                        {invoiceLabel ? (
                          <span className="inc-update-modal__file-name">{invoiceLabel}</span>
                        ) : null}
                      </div>
                      <Input
                        type="file"
                        id="invoice_file"
                        accept=".pdf,.png,.jpg,.jpeg"
                        onChange={(event) => setInvoiceFile(event.target.files?.[0] || null)}
                      />
                      <p className="inc-update-modal__hint">
                        Provider invoice (PDF). Stored in the IN folder.
                      </p>
                    </div>
                  </Col>
                  <Col md="6">
                    <div className="inc-update-modal__file-card inc-update-modal__file-card--raw">
                      <Label for="raw_file" className="fw-semibold">
                        Raw CSV
                      </Label>
                      <div className="inc-update-modal__file-status">
                        <span
                          className={`inc-update-modal__badge ${
                            record?.raw_file || record?.has_raw_file
                              ? "inc-update-modal__badge--ok"
                              : "inc-update-modal__badge--missing"
                          }`}
                        >
                          {record?.raw_file || record?.has_raw_file ? "Attached" : "Missing"}
                        </span>
                        {rawLabel ? (
                          <span className="inc-update-modal__file-name">{rawLabel}</span>
                        ) : null}
                      </div>
                      <Input
                        type="file"
                        id="raw_file"
                        accept=".csv,.txt,.tsv,.xlsx,.xls"
                        disabled={isSaving}
                        onChange={(event) => {
                          setRawFile(event.target.files?.[0] || null);
                          setRawUploadProgress(0);
                        }}
                      />
                      {isSaving && rawFile && rawUploadProgress > 0 ? (
                        <p className="inc-update-modal__hint mb-0 mt-2">
                          Uploading raw CSV to S3… {rawUploadProgress}%
                        </p>
                      ) : null}
                      <p className="inc-update-modal__hint">
                        Fuga raw CSV for Lambda / Process CSV. Replacing an existing file
                        overwrites it at the same S3 path and clears old Lambda output.
                      </p>
                    </div>
                  </Col>
                </Row>
              </div>
            </>
          )}
        </ModalBody>
        <ModalFooter className="d-flex justify-content-between">
          <Button type="button" color="light" onClick={toggle} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="submit" color="primary" disabled={isSaving || isDetailLoading}>
            {isSaving ? (
              <>
                <Spinner size="sm" className="me-2" /> Saving…
              </>
            ) : (
              "Save changes"
            )}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default UpdateIncomeModal;
